import React, { useState, useEffect, useCallback, useMemo } from 'react';
import SessionSquad from '../SessionSquad';
import { useSelector, useDispatch } from 'react-redux';
import { DEFAULT_TIME_BLOCKS, MENS_APPARATUS, WOMENS_APPARATUS } from '../../constants/competitionConstants';
import { decodeHtmlEntities } from '../../utils/textUtils';
import { AVAILABLE_LEVELS } from '../../constants/competitionConstants';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Paper,
  Divider,
  Button,
  Grid,
  Collapse
} from '@mui/material';
import { format, isValid } from 'date-fns';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import CloseIcon from '@mui/icons-material/Close';
import SessionDefinition from '../SessionDefinition';
import { updateCompetition, setToast } from '../../store/competitionSlice';
import { formatDate } from '../../utils/dateUtils';
import PrintIcon from '@mui/icons-material/Print';
import SessionPrintPreview from '../SessionPrintPreview';

const SessionDetails = ({ 
  session, 
  competitionColors,
  onNextSession,
  onPrevSession,
  hasNextSession,
  hasPrevSession,
  onDayClick,
  onSessionClick
}) => {
  const currentCompetition = useSelector(state => state.competition.currentCompetition);
  
  // Format time to show date from session.startDate with time in HH:MM AM/PM format
  const formatTime = (dateString) => {
    if (!dateString) return '';
    
    // Parse the time string (which may be a full datetime like "2026-01-23 08:50:00")
    const parsedDate = new Date(dateString);
    if (!isValid(parsedDate)) return dateString; // Return original if parsing fails
    
    // If there's a session start date, ensure we show that date with the time
    if (session.startDate) {
      // Get the date parts from session.startDate
      const sessionDate = new Date(session.startDate);
      if (isValid(sessionDate)) {
        // Create a new date using the session date but the time from dateString
        const timeHours = parsedDate.getHours();
        const timeMinutes = parsedDate.getMinutes();
        const timeSeconds = parsedDate.getSeconds();
        
        const resultDate = new Date(
          sessionDate.getFullYear(),
          sessionDate.getMonth(),
          sessionDate.getDate(),
          timeHours,
          timeMinutes,
          timeSeconds
        );
        
        // Format as HH:MM AM/PM
        return format(resultDate, 'h:mm a');
      }
    }
    
    // Fallback to formatDate if no session date is available
    return formatDate(dateString, 'h:mm a');
  };
  const [moveSquadsMode, setMoveSquadsMode] = useState(false);
  const [moveAthletesMode, setMoveAthletesMode] = useState(false);
  const [gymnastSnapshot, setGymnastSnapshot] = useState(null);
  const [rotationSnapshot, setRotationSnapshot] = useState(null);
  const [highlightedTeamId, setHighlightedTeamId] = useState(null);
  const [selectedRotation, setSelectedRotation] = useState(1);
  const [isEditing, setIsEditing] = useState(false);
  const [highlightTeamsEnabled, setHighlightTeamsEnabled] = useState(true);
  const [viewByTeams, setViewByTeams] = useState(false);
  const [highlightLevelsEnabled, setHighlightLevelsEnabled] = useState(false);
  const [teamColors, setTeamColors] = useState({});
  const [levelColors, setLevelColors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeFlight, setActiveFlight] = useState(1);
  const [selectedAthleteIds, setSelectedAthleteIds] = useState([]); // Lifted state for selection
  const [scratchMode, setScratchMode] = useState(false);
  const [scratchSelections, setScratchSelections] = useState([]);
  const [isPrintPreviewOpen, setIsPrintPreviewOpen] = useState(false);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState([]); // Array of { type: 'level', value: '3' } or { type: 'team', value: 'teamId' }

  // Handle Escape key to deselect athletes
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && moveAthletesMode) {
        setSelectedAthleteIds([]);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [moveAthletesMode]);

  // Handle keyboard shortcuts for rotations (1-6) and flights (A-F)
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Ignore if user is typing in an input field
      if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') {
        return;
      }

      const key = event.key.toUpperCase();

      // Handle rotation shortcuts (1-6 or whatever the max is)
      const rotation = currentCompetition?.rotations?.find(r => r.id === session.rotationId);
      const maxRotations = rotation?.numSquads || (session.gender?.toLowerCase() === 'm' ? 6 : 4);

      if (key >= '1' && key <= '9') {
        const rotationNum = parseInt(key);
        if (rotationNum >= 1 && rotationNum <= maxRotations) {
          setSelectedRotation(rotationNum);
          event.preventDefault();
        }
      }

      // Handle flight shortcuts (A, B, C, etc.)
      if (session.numFlights > 1) {
        const flightIndex = key.charCodeAt(0) - 65; // A=0, B=1, C=2, etc.
        if (flightIndex >= 0 && flightIndex < session.numFlights) {
          const flightLetter = String.fromCharCode(65 + flightIndex);
          const flightElement = document.getElementById(`flight-${flightLetter}`);
          if (flightElement) {
            const elementTop = flightElement.getBoundingClientRect().top + window.scrollY;
            const offsetTop = elementTop - 120;
            window.scrollTo({
              top: offsetTop,
              behavior: 'smooth'
            });
            event.preventDefault();
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [session.numFlights, session.rotationId, session.gender, currentCompetition]);

  // Track scroll position for sticky header
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY || document.documentElement.scrollTop;
      setIsScrolled(scrollPosition > 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Track which flight section is most visible
  useEffect(() => {
    if (session.numFlights <= 1) return;

    const handleScroll = () => {
      const flightElements = Array.from({ length: session.numFlights }, (_, i) => {
        const flightLetter = String.fromCharCode(65 + i);
        return document.getElementById(`flight-${flightLetter}`);
      }).filter(Boolean);

      if (flightElements.length === 0) return;

      const viewportHeight = window.innerHeight;
      const scrollY = window.scrollY;

      let maxVisibleArea = 0;
      let mostVisibleFlight = 1;

      flightElements.forEach((element, index) => {
        const rect = element.getBoundingClientRect();
        const elementTop = rect.top + scrollY;
        const elementBottom = rect.bottom + scrollY;

        // Calculate visible area of this element
        const visibleTop = Math.max(scrollY, elementTop);
        const visibleBottom = Math.min(scrollY + viewportHeight, elementBottom);
        const visibleArea = Math.max(0, visibleBottom - visibleTop);

        if (visibleArea > maxVisibleArea) {
          maxVisibleArea = visibleArea;
          mostVisibleFlight = index + 1;
        }
      });

      setActiveFlight(mostVisibleFlight);
    };

    window.addEventListener('scroll', handleScroll);
    // Also check on mount
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [session.numFlights]);

  // Reset scratch state when switching sessions
  useEffect(() => {
    setScratchMode(false);
    setScratchSelections([]);
  }, [session.sessionNumber]);

  // Memoize session gymnasts to avoid re-filtering on every render
  const sessionGymnasts = useMemo(() =>
    currentCompetition?.gymnasts?.filter(g => Number(g.session) === Number(session.sessionNumber)) || [],
    [currentCompetition?.gymnasts, session.sessionNumber]
  );

  // Curated palette of 40 maximally distinct colors - ANY two colors are visually distinct
  // This ensures no similar colors even when teams/levels are adjacent after moving athletes
  // Organized to maximize contrast between adjacent indices
  const DISTINCT_COLORS = useMemo(() => [
    '#E57373', // Red 300
    '#81C784', // Green 300
    '#64B5F6', // Blue 300
    '#FFD54F', // Amber 300
    '#BA68C8', // Purple 300
    '#4DB6AC', // Teal 300
    '#FF8A65', // Deep Orange 300
    '#A1887F', // Brown 300
    '#F06292', // Pink 300
    '#AED581', // Light Green 300
    '#4FC3F7', // Light Blue 300
    '#FFB74D', // Orange 300
    '#9575CD', // Deep Purple 300
    '#4DD0E1', // Cyan 300
    '#DCE775', // Lime 300
    '#90A4AE', // Blue Grey 300
    '#EF5350', // Red 400
    '#66BB6A', // Green 400
    '#42A5F5', // Blue 400
    '#FFCA28', // Amber 400
    '#AB47BC', // Purple 400
    '#26A69A', // Teal 400
    '#FF7043', // Deep Orange 400
    '#8D6E63', // Brown 400
    '#EC407A', // Pink 400
    '#9CCC65', // Light Green 400
    '#29B6F6', // Light Blue 400
    '#FFA726', // Orange 400
    '#7E57C2', // Deep Purple 400
    '#26C6DA', // Cyan 400
    '#D4E157', // Lime 400
    '#78909C', // Blue Grey 400
    '#EF9A9A', // Red 200
    '#A5D6A7', // Green 200
    '#90CAF9', // Blue 200
    '#FFE082', // Amber 200
    '#CE93D8', // Purple 200
    '#80CBC4', // Teal 200
    '#FFAB91', // Deep Orange 200
    '#BCAAA4', // Brown 200
  ], []);

  const generateDistinctColor = useCallback((index) => {
    return DISTINCT_COLORS[index % DISTINCT_COLORS.length];
  }, [DISTINCT_COLORS]);

  // Generate team colors when highlighting is enabled
  useEffect(() => {
    if (highlightTeamsEnabled) {
      const teams = new Set(sessionGymnasts.map(g => g.teamId));
      const sortedTeams = [...teams].sort();

      const newTeamColors = {};
      sortedTeams.forEach((teamId, index) => {
        newTeamColors[teamId] = generateDistinctColor(index);
      });
      setTeamColors(newTeamColors);
    } else {
      setTeamColors({});
    }
  }, [highlightTeamsEnabled, sessionGymnasts, generateDistinctColor]);

  // Generate level colors when level highlighting is enabled
  useEffect(() => {
    if (highlightLevelsEnabled) {
      const levels = new Set(sessionGymnasts.map(g => g.level));
      const sortedLevels = [...levels].sort();

      const newLevelColors = {};
      sortedLevels.forEach((level, index) => {
        newLevelColors[level] = generateDistinctColor(index);
      });
      setLevelColors(newLevelColors);
    } else {
      setLevelColors({});
    }
  }, [highlightLevelsEnabled, sessionGymnasts, generateDistinctColor]);
  const dispatch = useDispatch();
  const toggleScratchSelection = useCallback((athleteId) => {
    setScratchSelections(prev => {
      const next = new Set(prev);
      if (next.has(athleteId)) {
        next.delete(athleteId);
      } else {
        next.add(athleteId);
      }
      return Array.from(next);
    });
  }, []);

  const hasAthleteOrderChanges = useCallback(() => {
    if (!gymnastSnapshot) return false;
    const currentGymnasts = currentCompetition?.gymnasts || [];

    if (currentGymnasts.length !== gymnastSnapshot.length) return true;

    const snapshotMap = new Map();
    gymnastSnapshot.forEach(g => {
      if (!g?.id) return;
      snapshotMap.set(g.id, `${g.squad || ''}|${g.flight || ''}|${g.order || ''}`);
    });

    for (const g of currentGymnasts) {
      if (!g?.id) continue;
      const signature = `${g.squad || ''}|${g.flight || ''}|${g.order || ''}`;
      if (snapshotMap.get(g.id) !== signature) {
        return true;
      }
    }
    return false;
  }, [gymnastSnapshot, currentCompetition?.gymnasts]);

  const hasSquadOrderChanges = useCallback(() => {
    if (!rotationSnapshot) return false;
    const currentRotations = currentCompetition?.rotations || [];
    const currentRotation = currentRotations.find(r => r.id === session.rotationId);
    const snapshotRotation = rotationSnapshot.find(r => r.id === session.rotationId);
    if (!currentRotation || !snapshotRotation) return false;

    const currentAssignments = (currentRotation.squadAssignments || []).join('|');
    const snapshotAssignments = (snapshotRotation.squadAssignments || []).join('|');
    return currentAssignments !== snapshotAssignments;
  }, [rotationSnapshot, currentCompetition?.rotations, session.rotationId]);

  const handleEditSave = (updatedSession) => {
    // Show immediate "Saving..." toast
    dispatch(setToast({ message: 'Saving session changes...', severity: 'info' }));

    // Normalize timeBlock keys to remove case duplicates (warmupStart vs warmUpStart)
    const normalizedTimeBlocks = {};
    const canonicalKeys = DEFAULT_TIME_BLOCKS.map(b => b.id);

    // Merge existing and updated timeBlocks
    const allTimeBlocks = {
      ...session.timeBlocks,
      ...updatedSession.timeBlocks
    };

    // Only keep canonical keys (removes old lowercase variants)
    Object.entries(allTimeBlocks).forEach(([key, value]) => {
      if (canonicalKeys.includes(key)) {
        normalizedTimeBlocks[key] = value;
      }
    });

    dispatch(updateCompetition({
      type: 'session',
      sessionNumber: session.sessionNumber,
      data: {
        ...session,
        ...updatedSession,
        timeBlocks: normalizedTimeBlocks,
        rotationId: updatedSession.rotationId || session.rotationId
      },
      showToast: true
    }));
    setIsEditing(false);
  };

  const handleDeleteSession = (sessionToDelete) => {
    // Get all sessions
    const allSessions = currentCompetition?.sessions || [];

    // Filter out the session to delete
    const remainingSessions = allSessions.filter(
      s => s.sessionNumber !== sessionToDelete.sessionNumber
    );

    // Also remove any gymnasts assigned to this session
    const remainingGymnasts = (currentCompetition?.gymnasts || []).filter(
      g => Number(g.session) !== Number(sessionToDelete.sessionNumber)
    );

    // Dispatch update to remove the session
    dispatch(updateCompetition({
      type: 'sessions',
      data: remainingSessions,
      showToast: true,
      toastMessage: `Session ${sessionToDelete.sessionNumber} deleted`
    }));

    // Update gymnasts if any were assigned
    if (remainingGymnasts.length !== (currentCompetition?.gymnasts || []).length) {
      dispatch(updateCompetition({
        type: 'gymnasts',
        data: remainingGymnasts
      }));
    }

    setIsEditing(false);

    // Navigate to another session if available
    if (remainingSessions.length > 0) {
      // Try to navigate to next or previous session
      if (hasNextSession) {
        onNextSession?.();
      } else if (hasPrevSession) {
        onPrevSession?.();
      }
    }
  };
  
  // Find the rotation that matches this session's rotationId
  const rotation = currentCompetition?.rotations?.find(
    r => r.id === session.rotationId
  );
  
  // Infer gender from rotation if not set
  const getDisplayGender = () => {
    if (session.gender) return session.gender;
    if (rotation?.name?.toLowerCase().includes('men')) return 'M';
    if (rotation?.name?.toLowerCase().includes('women')) return 'F';
    return 'All';
  };

  // Get the squad order for the current rotation
  const getSquadOrder = (rotationNum) => {
    if (!rotation?.squadAssignments || rotationNum < 1) return null;
    const orderString = rotation.squadAssignments[rotationNum - 1] || rotation.squadAssignments[0];
    return orderString ? orderString.split('').map(num => parseInt(num)) : null;
  };

  const handleScratchButton = () => {
    const sessionGymnasts = currentCompetition?.gymnasts
      ?.filter(g => Number(g.session) === Number(session.sessionNumber)) || [];

    if (sessionGymnasts.length === 0) {
      dispatch(setToast({ message: 'No gymnasts found for this session', severity: 'warning' }));
      setScratchMode(false);
      setScratchSelections([]);
      return;
    }

    if (!scratchMode) {
      setScratchMode(true);
      setMoveAthletesMode(false);
      setMoveSquadsMode(false);
      setSelectedAthleteIds([]);
      setScratchSelections([]);
      return;
    }

    const previousScratched = new Set(sessionGymnasts.filter(g => g.scratched).map(g => g.id));
    const targetScratches = new Set(scratchSelections);

    const updatedSessionGymnasts = sessionGymnasts.map(g => {
      const shouldScratch = g.scratched || targetScratches.has(g.id);
      return {
        ...g,
        scratched: shouldScratch,
        ScratchDate: shouldScratch ? (g.ScratchDate || new Date().toISOString()) : null
      };
    });

    const otherGymnasts = currentCompetition?.gymnasts
      ?.filter(g => Number(g.session) !== Number(session.sessionNumber)) || [];

    const newlyScratched = updatedSessionGymnasts.filter(g => targetScratches.has(g.id) && !previousScratched.has(g.id));

    const formatList = (list) => {
      if (list.length === 0) return '';
      const names = list
        .filter(g => g.firstName || g.lastName)
        .slice(0, 3)
        .map(g => `${g.firstName || ''} ${g.lastName || ''}`.trim())
        .join(', ');
      const remaining = list.length - 3;
      return remaining > 0 ? `${names}${names ? ' ' : ''}+${remaining} more` : names;
    };

    const messageParts = [];
    if (newlyScratched.length) {
      const names = formatList(newlyScratched);
      messageParts.push(`Scratched ${newlyScratched.length} athlete${newlyScratched.length === 1 ? '' : 's'}${names ? ` (${names})` : ''}`);
    }

    const toastMessage = messageParts.join(' • ') || 'No scratch changes made';

    dispatch(updateCompetition({
      type: 'gymnasts',
      data: [...otherGymnasts, ...updatedSessionGymnasts],
      showToast: true,
      toastMessage
    }));

    setScratchMode(false);
    setScratchSelections([]);
  };

  const renderFlightSection = (flightNumber) => {
    // SessionSquad now handles the flight header in all modes
    return (
      <Box>
        <SessionSquad
          session={session}
          competitionColors={competitionColors}
          flightNumber={flightNumber}
          highlightedTeamId={highlightedTeamId}
          onTeamHighlight={setHighlightedTeamId}
          selectedRotation={selectedRotation}
          highlightTeamsEnabled={highlightTeamsEnabled}
          highlightLevelsEnabled={highlightLevelsEnabled}
          teamColors={teamColors}
          levelColors={levelColors}
          moveSquadsMode={moveSquadsMode}
          moveAthletesMode={moveAthletesMode}
          scratchMode={scratchMode}
          scratchSelections={scratchSelections}
          selectedAthleteIds={selectedAthleteIds}
          setSelectedAthleteIds={setSelectedAthleteIds}
          onToggleScratch={toggleScratchSelection}
          selectedFilters={selectedFilters}
          viewByTeams={viewByTeams}
          onUpdateOrders={(updatedGymnasts) => {
            dispatch(updateCompetition({
              type: 'gymnasts',
              data: updatedGymnasts,
              showToast: true
            }));
          }}
        />
      </Box>
    );
  };

  if (isEditing) {
    return (
      <SessionDefinition
        initialData={{
          ...session,
          gender: session.gender,
          timeBlocks: session.timeBlocks || {},
          enabledTimeBlocks: session.enabledTimeBlocks || DEFAULT_TIME_BLOCKS.map(b => b.id),
          selectedLevels: session.levels || [],
          rotationId: session.rotationId
        }}
        availableRotations={currentCompetition?.rotations || []}
        onSave={handleEditSave}
        onCancel={() => setIsEditing(false)}
        onDelete={handleDeleteSession}
        editMode={true}
        sessionDate={session.startDate}
      />
    );
  }
  
  const getWatermarkSvg = (text) => `url("data:image/svg+xml,%3Csvg width='150' height='150' viewBox='0 0 150 150' xmlns='http://www.w3.org/2000/svg'%3E%3Ctext x='75' y='75' fill='%23808080' fill-opacity='0.15' font-family='Arial, sans-serif' font-weight='bold' font-size='24' transform='rotate(-45 75 75)' text-anchor='middle' dominant-baseline='middle'%3E${text}%3C/text%3E%3C/svg%3E")`;

  return (
    <Box data-ui="sessions-content">
      {/* Compact Session Header - single line */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: 1.5,
          pb: 0.75,
          borderBottom: 1,
          borderColor: 'divider'
        }}
        data-ui="sessions-header"
      >
        {/* Left: Session Navigation */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} data-ui="sessions-navigation">
          <Typography variant="h6" sx={{ mb: 0, fontSize: '20px' }}>
            Session {session.sessionNumber}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            <IconButton
              onClick={onPrevSession}
              disabled={!hasPrevSession}
              size="small"
              className="no-print"
              data-ui="sessions-prev-button"
              sx={{
                // In dark mode, use white for better contrast
                color: theme => hasPrevSession
                  ? (theme.palette.mode === 'dark' ? '#fff' : competitionColors.primary)
                  : 'action.disabled',
                minWidth: 'auto',
                width: 28,
                height: 28
              }}
            >
              <NavigateBeforeIcon fontSize="small" />
            </IconButton>
            <IconButton
              onClick={onNextSession}
              disabled={!hasNextSession}
              size="small"
              className="no-print"
              data-ui="sessions-next-button"
              sx={{
                // In dark mode, use white for better contrast
                color: theme => hasNextSession
                  ? (theme.palette.mode === 'dark' ? '#fff' : competitionColors.primary)
                  : 'action.disabled',
                minWidth: 'auto',
                width: 28,
                height: 28
              }}
            >
              <NavigateNextIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* Right: Compact Day/Session Selector */}
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            overflowX: 'auto',
            alignItems: 'center',
            flex: 1,
            justifyContent: 'flex-end',
            maxWidth: '75%'
          }}
          data-ui="sessions-day-selector"
        >
          {Object.entries(session.sessionsByDay || {}).map(([date, daySessions]) => {
            const isCurrentDay = date === (session.startDate?.split(' ')[0] || session.timeBlocks?.competitionStart?.split(' ')[0]);
            return (
              <Box
                key={date}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  px: 1,
                  py: 0.25,
                  bgcolor: isCurrentDay ? `${competitionColors.primary}11` : 'transparent',
                  borderRadius: 1,
                  border: isCurrentDay ? `1px solid ${competitionColors.primary}` : '1px solid transparent'
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    cursor: 'pointer',
                    // In dark mode, use white text for selected day for better contrast
                    color: theme => isCurrentDay
                      ? (theme.palette.mode === 'dark' ? '#fff' : competitionColors.primary)
                      : 'text.secondary',
                    fontWeight: isCurrentDay ? 600 : 400,
                    fontSize: '0.875rem',
                    whiteSpace: 'nowrap',
                    lineHeight: 1.2,
                    '&:hover': {
                      color: theme => theme.palette.mode === 'dark' ? '#fff' : competitionColors.primary
                    }
                  }}
                  onClick={() => onDayClick?.(date)}
                >
                  {formatDate(date, 'EEE, MMM d')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.25 }}>
                  {daySessions.map((sessionNum) => (
                    <Chip
                      key={sessionNum}
                      label={sessionNum}
                      size="small"
                      onClick={() => {
                        if (sessionNum !== session.sessionNumber) {
                          const targetSession = session.sessionsByDay[date].find(s => s === sessionNum);
                          if (targetSession) {
                            onSessionClick?.(targetSession);
                          }
                        }
                      }}
                      sx={{
                        cursor: 'pointer',
                        height: 20,
                        minWidth: 24,
                        '& .MuiChip-label': {
                          px: 0.5,
                          py: 0,
                          fontSize: '0.7rem',
                          lineHeight: 1
                        },
                        backgroundColor: sessionNum === session.sessionNumber ? competitionColors.primary : 'transparent',
                        color: sessionNum === session.sessionNumber ? '#ffffff' : 'text.secondary',
                        fontWeight: sessionNum === session.sessionNumber ? 700 : 400,
                        border: `1px solid ${sessionNum === session.sessionNumber ? competitionColors.primary : 'rgba(0,0,0,0.23)'}`,
                        '&:hover': {
                          backgroundColor: sessionNum === session.sessionNumber
                            ? competitionColors.primary
                            : `${competitionColors.primary}22`,
                          color: sessionNum === session.sessionNumber ? 'white' : competitionColors.primary,
                        }
                      }}
                    />
                  ))}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Session Details Card */}
      <Card sx={{ mb: 3 }} data-ui="sessions-details-card">
        <CardContent sx={{ width: '100%', boxSizing: 'border-box' }}>
          {/* First Line - Full width info bar OUTSIDE of Grid */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            gap: 2,
            flexWrap: 'wrap',
            mb: 2,
            pb: 1.5,
            borderBottom: 1,
            borderColor: 'divider'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              {session.description && (
                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                  {session.description}
                </Typography>
              )}

                    {/* Levels */}
                    {session.levels?.length > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" color="text.secondary">Levels:</Typography>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {session.levels.map((level, index) => (
                            <Chip
                              key={index}
                              label={level}
                              size="small"
                              variant="outlined"
                              sx={{
                                borderColor: competitionColors.primary,
                                backgroundColor: `${competitionColors.primary}11`
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}

                    {/* Gender */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" color="text.secondary">Gender:</Typography>
                      <Chip
                        label={getDisplayGender()}
                        size="small"
                        sx={{
                          backgroundColor: `${competitionColors.primary}22`,
                          borderColor: competitionColors.primary
                        }}
                      />
                    </Box>

                    {/* Athletes Count - calculated from actual gymnasts */}
                    {(() => {
                      const sessionAthleteCount = currentCompetition?.gymnasts
                        ?.filter(g => Number(g.session) === Number(session.sessionNumber) && !g.scratched)?.length || 0;

                      if (sessionAthleteCount === 0) return null;

                      return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" color="text.secondary">Athletes:</Typography>
                          <Chip
                            label={sessionAthleteCount}
                            size="small"
                            sx={{
                              backgroundColor: `${competitionColors.primary}22`,
                              borderColor: competitionColors.primary
                            }}
                          />
                        </Box>
                      );
                    })()}

                    {/* Teams Count */}
                    {(() => {
                      const sessionGymnasts = currentCompetition?.gymnasts
                        ?.filter(g => Number(g.session) === Number(session.sessionNumber) && !g.scratched);

                      if (!sessionGymnasts || sessionGymnasts.length === 0) return null;

                      const uniqueTeams = new Set(sessionGymnasts
                        .map(g => g.teamId)
                        .filter(teamId => teamId));

                      const teamCount = uniqueTeams.size;

                      if (teamCount === 0) return null;

                      return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" color="text.secondary">Teams:</Typography>
                          <Chip
                            label={teamCount}
                            size="small"
                            sx={{
                              backgroundColor: `${competitionColors.primary}22`,
                              borderColor: competitionColors.primary
                            }}
                          />
                        </Box>
                      );
                    })()}
                  </Box>

            {/* Session Type Chip - pushed to right */}
            <Chip
              label={session.type || (session.numFlights > 1 ? 'Capital Cup' : 'Standard')}
              size="small"
              sx={{
                backgroundColor: `${competitionColors.secondary}22`,
                borderColor: competitionColors.secondary,
                flexShrink: 0
              }}
            />
          </Box>

          {/* Breakdown Section (collapsible) */}
          <Collapse in={showBreakdown} timeout={300}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', py: 1, mb: 1 }}>
                      {/* Athletes by Level Count */}
                      {(() => {
                        // Get gymnasts for this session and group by level
                        const sessionGymnasts = currentCompetition?.gymnasts
                          ?.filter(g => Number(g.session) === Number(session.sessionNumber));

                        if (!sessionGymnasts || sessionGymnasts.length === 0) return null;

                        // Group by level and count
                        const levelCounts = {};
                        sessionGymnasts.forEach(gymnast => {
                          if (gymnast.level) {
                            levelCounts[gymnast.level] = (levelCounts[gymnast.level] || 0) + 1;
                          }
                        });

                        // Convert to array and sort levels
                        const levelEntries = Object.entries(levelCounts).sort(([levelA], [levelB]) => {
                          // Sort numerically if levels are numbers, otherwise alphabetically
                          const numA = Number(levelA);
                          const numB = Number(levelB);
                          if (!isNaN(numA) && !isNaN(numB)) {
                            return numA - numB;
                          }
                          return levelA.localeCompare(levelB);
                        });

                        if (levelEntries.length === 0) return null;

                        // Curated palette of 40 maximally distinct colors
                        const distinctColors = [
                          '#E57373', '#81C784', '#64B5F6', '#FFD54F', '#BA68C8', '#4DB6AC', '#FF8A65', '#A1887F',
                          '#F06292', '#AED581', '#4FC3F7', '#FFB74D', '#9575CD', '#4DD0E1', '#DCE775', '#90A4AE',
                          '#EF5350', '#66BB6A', '#42A5F5', '#FFCA28', '#AB47BC', '#26A69A', '#FF7043', '#8D6E63',
                          '#EC407A', '#9CCC65', '#29B6F6', '#FFA726', '#7E57C2', '#26C6DA', '#D4E157', '#78909C',
                          '#EF9A9A', '#A5D6A7', '#90CAF9', '#FFE082', '#CE93D8', '#80CBC4', '#FFAB91', '#BCAAA4'
                        ];
                        const levelColorsLocal = {};
                        levelEntries.forEach(([level], index) => {
                          levelColorsLocal[level] = distinctColors[index % distinctColors.length];
                        });

                        return (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="body2" color="text.secondary">By Level:</Typography>
                            {levelEntries.map(([level, count]) => {
                              const isSelected = selectedFilters.some(f => f.type === 'level' && f.value === level);

                              // Check if this level's athletes would be visible with current filters
                              const hasActiveFilters = selectedFilters.length > 0;
                              const isLevelVisible = !hasActiveFilters || selectedFilters.every(f => {
                                if (f.type === 'level') return f.value === level;
                                // For team filters, check if any athletes match both this level and the filtered team
                                if (f.type === 'team') {
                                  return sessionGymnasts.some(g => g.level === level && String(g.teamId) === String(f.value));
                                }
                                return true;
                              });

                              return (
                                <Chip
                                  key={level}
                                  label={`${level} (${count})`}
                                  size="small"
                                  onClick={() => {
                                    if (isSelected) {
                                      // Remove this filter
                                      setSelectedFilters(prev => prev.filter(f => !(f.type === 'level' && f.value === level)));
                                    } else {
                                      // Add this filter
                                      setSelectedFilters(prev => [...prev, { type: 'level', value: level }]);
                                    }
                                  }}
                                  sx={{
                                    cursor: 'pointer',
                                    backgroundColor: isSelected
                                      ? (highlightLevelsEnabled && levelColorsLocal[level] ? levelColorsLocal[level] : competitionColors.primary)
                                      : (highlightLevelsEnabled && isLevelVisible && levelColorsLocal[level])
                                        ? `${levelColorsLocal[level]}99`
                                        : `${competitionColors.primary}22`,
                                    color: isSelected ? '#fff' : 'inherit',
                                    borderColor: isSelected
                                      ? (highlightLevelsEnabled && levelColorsLocal[level] ? levelColorsLocal[level] : competitionColors.primary)
                                      : (highlightLevelsEnabled && isLevelVisible && levelColorsLocal[level])
                                        ? levelColorsLocal[level]
                                        : competitionColors.primary,
                                    borderWidth: isSelected ? 2 : 1,
                                    fontWeight: isSelected ? 700 : 400,
                                    '&:hover': {
                                      backgroundColor: isSelected
                                        ? (highlightLevelsEnabled && levelColorsLocal[level] ? levelColorsLocal[level] : competitionColors.primary)
                                        : (highlightLevelsEnabled && isLevelVisible && levelColorsLocal[level])
                                          ? levelColorsLocal[level]
                                          : `${competitionColors.primary}44`,
                                    }
                                  }}
                                />
                              );
                            })}
                          </Box>
                        );
                      })()}
                      {/* Athletes by Flight Count */}
                      {(() => {
                        const sessionGymnasts = currentCompetition?.gymnasts
                          ?.filter(g => Number(g.session) === Number(session.sessionNumber));

                        if (!sessionGymnasts || sessionGymnasts.length === 0) return null;

                        const flightCounts = {};
                        sessionGymnasts.forEach(gymnast => {
                          if (gymnast.flight) {
                            flightCounts[gymnast.flight] = (flightCounts[gymnast.flight] || 0) + 1;
                          }
                        });

                        const flightEntries = Object.entries(flightCounts).sort(([flightA], [flightB]) =>
                          flightA.localeCompare(flightB)
                        );

                        if (flightEntries.length === 0) return null;

                        return (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', ml: 2 }}>
                            <Typography variant="body2" color="text.secondary">By Flight:</Typography>
                            {flightEntries.map(([flight, count]) => {
                              const isSelected = selectedFilters.some(f => f.type === 'flight' && f.value === flight);

                              const hasActiveFilters = selectedFilters.length > 0;
                              const isFlightVisible = !hasActiveFilters || selectedFilters.every(f => {
                                if (f.type === 'flight') return f.value === flight;
                                if (f.type === 'level') {
                                  return sessionGymnasts.some(g => g.flight === flight && g.level === f.value);
                                }
                                if (f.type === 'team') {
                                  return sessionGymnasts.some(g => g.flight === flight && String(g.teamId) === String(f.value));
                                }
                                return true;
                              });

                              return (
                                <Chip
                                  key={flight}
                                  label={`${flight} (${count})`}
                                  size="small"
                                  onClick={() => {
                                    if (isSelected) {
                                      setSelectedFilters(prev => prev.filter(f => !(f.type === 'flight' && f.value === flight)));
                                    } else {
                                      setSelectedFilters(prev => [...prev, { type: 'flight', value: flight }]);
                                    }
                                  }}
                                  sx={{
                                    cursor: 'pointer',
                                    backgroundColor: isSelected
                                      ? competitionColors.primary
                                      : isFlightVisible
                                        ? `${competitionColors.primary}22`
                                        : `${competitionColors.primary}08`, // More subtle if not visible and not selected
                                    color: isSelected ? '#fff' : 'inherit',
                                    borderColor: competitionColors.primary,
                                    borderWidth: isSelected ? 2 : 1,
                                    fontWeight: isSelected ? 700 : 400,
                                    '&:hover': {
                                      backgroundColor: isSelected
                                        ? competitionColors.primary
                                        : `${competitionColors.primary}44`,
                                    }
                                  }}
                                />
                              );
                            })}
                          </Box>
                        );
                      })()}
                      {/* Athletes by Team Count */}
                      {(() => {
                        // Get gymnasts for this session and group by team
                        const sessionGymnasts = currentCompetition?.gymnasts
                          ?.filter(g => Number(g.session) === Number(session.sessionNumber));

                        if (!sessionGymnasts || sessionGymnasts.length === 0) return null;

                        // Group by team and count
                        const teamCounts = {};
                        sessionGymnasts.forEach(gymnast => {
                          if (gymnast.teamId) {
                            teamCounts[gymnast.teamId] = (teamCounts[gymnast.teamId] || 0) + 1;
                          }
                        });

                        // Convert to array and get team short names
                        const teamEntries = Object.entries(teamCounts)
                          .map(([teamId, count]) => {
                            // Find team short name from teams array
                            const team = currentCompetition?.teams?.find(t => String(t.id) === String(teamId));
                            return {
                              teamId,
                              teamName: decodeHtmlEntities(team?.shortName || team?.name) || `Team ${teamId}`,
                              count
                            };
                          })
                          .sort((a, b) => a.teamName.localeCompare(b.teamName));

                        if (teamEntries.length === 0) return null;

                        // Curated palette of 40 maximally distinct colors
                        const distinctColors = [
                          '#E57373', '#81C784', '#64B5F6', '#FFD54F', '#BA68C8', '#4DB6AC', '#FF8A65', '#A1887F',
                          '#F06292', '#AED581', '#4FC3F7', '#FFB74D', '#9575CD', '#4DD0E1', '#DCE775', '#90A4AE',
                          '#EF5350', '#66BB6A', '#42A5F5', '#FFCA28', '#AB47BC', '#26A69A', '#FF7043', '#8D6E63',
                          '#EC407A', '#9CCC65', '#29B6F6', '#FFA726', '#7E57C2', '#26C6DA', '#D4E157', '#78909C',
                          '#EF9A9A', '#A5D6A7', '#90CAF9', '#FFE082', '#CE93D8', '#80CBC4', '#FFAB91', '#BCAAA4'
                        ];
                        const teamColorsLocal = {};
                        teamEntries.forEach(({ teamId }, index) => {
                          teamColorsLocal[teamId] = distinctColors[index % distinctColors.length];
                        });

                        return (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="body2" color="text.secondary">By Team:</Typography>
                            {teamEntries.map(({ teamId, teamName, count }) => {
                              const isSelected = selectedFilters.some(f => f.type === 'team' && f.value === teamId);

                              // Check if this team's athletes would be visible with current filters
                              const hasActiveFilters = selectedFilters.length > 0;
                              const isTeamVisible = !hasActiveFilters || selectedFilters.every(f => {
                                if (f.type === 'team') return f.value === teamId;
                                // For level filters, check if any athletes match both this team and the filtered level
                                if (f.type === 'level') {
                                  return sessionGymnasts.some(g => String(g.teamId) === String(teamId) && g.level === f.value);
                                }
                                return true;
                              });

                              return (
                                <Chip
                                  key={teamId}
                                  label={`${teamName} (${count})`}
                                  size="small"
                                  onClick={() => {
                                    if (isSelected) {
                                      // Remove this filter
                                      setSelectedFilters(prev => prev.filter(f => !(f.type === 'team' && f.value === teamId)));
                                    } else {
                                      // Add this filter
                                      setSelectedFilters(prev => [...prev, { type: 'team', value: teamId }]);
                                    }
                                  }}
                                  sx={{
                                    cursor: 'pointer',
                                    backgroundColor: isSelected
                                      ? (highlightTeamsEnabled && teamColorsLocal[teamId] ? teamColorsLocal[teamId] : competitionColors.primary)
                                      : (highlightTeamsEnabled && isTeamVisible && teamColorsLocal[teamId])
                                        ? `${teamColorsLocal[teamId]}99`
                                        : `${competitionColors.primary}22`,
                                    color: isSelected ? '#fff' : 'inherit',
                                    borderColor: isSelected
                                      ? (highlightTeamsEnabled && teamColorsLocal[teamId] ? teamColorsLocal[teamId] : competitionColors.primary)
                                      : (highlightTeamsEnabled && isTeamVisible && teamColorsLocal[teamId])
                                        ? teamColorsLocal[teamId]
                                        : competitionColors.primary,
                                    borderWidth: isSelected ? 2 : 1,
                                    fontWeight: isSelected ? 700 : 400,
                                    '&:hover': {
                                      backgroundColor: isSelected
                                        ? (highlightTeamsEnabled && teamColorsLocal[teamId] ? teamColorsLocal[teamId] : competitionColors.primary)
                                        : (highlightTeamsEnabled && isTeamVisible && teamColorsLocal[teamId])
                                          ? teamColorsLocal[teamId]
                                          : `${competitionColors.primary}44`,
                                    }
                                  }}
                                />
                              );
                            })}
                          </Box>
                        );
                      })()}
                    </Box>
          </Collapse>

          {/* Grid for Schedule, Rotation, Actions */}
          <Grid
            container
            spacing={0}
            sx={{
              '& .MuiGrid-item': {
                display: 'flex',
                flexDirection: 'column'
              }
            }}
          >
            {/* Basic Info Section */}
            <Grid item xs={12} md={4} sx={{ flex: 1, p: 0.5 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0, pr: { md: 3 } }}>
                {/* Time Blocks */}
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25 }}>
                    Schedule
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                    {session.timeBlocks && (() => {
                      // Helper to parse time strings with reference date
                      const parseTimeString = (timeStr) => {
                        if (!timeStr) return null;
                        
                        // Try parsing as full ISO date first
                        let date = new Date(timeStr);
                        if (!isNaN(date.getTime())) {
                          return date;
                        }
                        
                        // Parse time-only strings like "8:40 AM" or "08:40"
                        const timeMatch = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
                        if (timeMatch) {
                          let hours = parseInt(timeMatch[1]);
                          const minutes = parseInt(timeMatch[2]);
                          const isPM = timeMatch[3]?.toUpperCase() === 'PM';
                          
                          // Convert to 24-hour format
                          if (isPM && hours !== 12) hours += 12;
                          if (!isPM && hours === 12) hours = 0;
                          
                          // Create date with reference date (today) and parsed time
                          date = new Date();
                          date.setHours(hours, minutes, 0, 0);
                          return date;
                        }
                        
                        return null;
                      };
                      
                      // Only use canonical timeBlock keys to avoid duplicates (warmupStart vs warmUpStart)
                      const canonicalKeys = DEFAULT_TIME_BLOCKS.map(b => b.id);
                      
                      const entries = Object.entries(session.timeBlocks)
                        .filter(([key, time]) => time && canonicalKeys.includes(key)) // Only canonical keys
                        .map(([name, time]) => {
                          const parsed = parseTimeString(time);
                          return { name, time, parsed, timestamp: parsed?.getTime() || 0 };
                        })
                        .filter(entry => entry.parsed !== null); // Remove invalid times
                      
                      // Sort by time ascending
                      const sorted = entries.sort((a, b) => a.timestamp - b.timestamp);

                      const labelMap = {
                        openStretchStart: { text: 'Open Stretch', style: 'italic' },
                        warmUpStart: { text: 'Warm Up', style: 'italic' },
                        marchInStart: { text: 'March In', style: 'italic' },
                        competitionStart: { text: 'Start', style: 'bold' },
                        competitionEnd: { text: 'End', style: 'bold' },
                        awardsEnd: { text: 'Awards End', style: 'italic' },
                      };

                      return sorted.map(({ name: block, time }) => {
                        const meta = labelMap[block] || { 
                          text: block.replace(/([A-Z])/g, ' $1').trim().replace(/^\w/, c => c.toUpperCase()),
                          style: 'normal'
                        };
                        const isBold = meta.style === 'bold';
                        const isItalic = meta.style === 'italic';
                        return (
                          <Box 
                            key={block} 
                            sx={{ 
                              display: 'grid',
                              gridTemplateColumns: 'auto 96px',
                              columnGap: 1,
                              alignItems: 'center'
                            }}
                          >
                            <Typography 
                              variant="body2" 
                              color="text.secondary"
                              sx={{ fontWeight: isBold ? 700 : 400, fontStyle: isItalic ? 'italic' : 'normal' }}
                            >
                              {meta.text}:
                            </Typography>
                            <Typography 
                              variant="body2" 
                              sx={{ fontWeight: isBold ? 700 : 400, textAlign: 'right' }}
                            >
                              {formatTime(time)}
                            </Typography>
                          </Box>
                        );
                      });
                    })()}
                  </Box>
                </Box>

                {/* Additional Info */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0, mt: 0.5 }}>
                  {session.venue && (
                    <Typography variant="body2">
                      <strong>Venue:</strong> {session.venue}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Grid>

            {/* Rotation Information Section */}
            <Grid item xs={12} md={4} sx={{ flex: 1, p: 0.5 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                {rotation ? (
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25 }}>
                      Rotation
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                      <Typography variant="body2">
                        {rotation.name}
                      </Typography>
                      {/* Flights first */}
                      <Typography variant="body2">
                        Flights: {session.numFlights || 1}
                      </Typography>
                      {/* Squads per flight with athlete counts */}
                      {(() => {
                        const sessionGymnasts = currentCompetition?.gymnasts
                          ?.filter(g => Number(g.session) === Number(session.sessionNumber)) || [];
                        const totalSquads = rotation?.numSquads || 0;
                        const numFlights = session.numFlights || 1;
                        const squadsPerFlight = numFlights > 0 ? (totalSquads / numFlights).toFixed(0) : 0;

                        // Calculate min/max/avg athletes per squad
                        const squadAthleteCounts = {}; // Key: "flight-squad", Value: count
                        sessionGymnasts.forEach(gymnast => {
                          if (gymnast.flight && gymnast.squad) {
                            const key = `${gymnast.flight}-${gymnast.squad}`;
                            squadAthleteCounts[key] = (squadAthleteCounts[key] || 0) + 1;
                          }
                        });

                        const counts = Object.values(squadAthleteCounts);
                        const minAthletesPerSquad = counts.length > 0 ? Math.min(...counts) : 0;
                        const maxAthletesPerSquad = counts.length > 0 ? Math.max(...counts) : 0;
                        const avgAthletesPerSquad = counts.length > 0 ? (counts.reduce((sum, count) => sum + count, 0) / counts.length).toFixed(1) : 0;

                        return (
                          <Typography variant="body2">
                            Squads: {rotation.numSquads} / flight 
                            (Min: {minAthletesPerSquad}, Max: {maxAthletesPerSquad}, Avg: {avgAthletesPerSquad})
                          </Typography>
                        );
                      })()}
                      {rotation.squadAssignments && (
                        <Box>
                          <Typography variant="body2">
                            Squad Order:
                          </Typography>
                          <Box
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: rotation.squadAssignments.length <= 4
                                ? 'repeat(4, 1fr)'   // 1x4 layout for 4 or fewer items
                                : 'repeat(3, 1fr)',  // 2x3 layout for more than 4 items (typically 6)
                              gap: 1,
                              mt: 0.5,
                              width: '100%'
                            }}
                          >
                            {rotation.squadAssignments.map((order, idx) => (
                              <Chip
                                key={idx}
                                label={`R${idx + 1}: ${order}`}
                                size="small"
                                sx={{
                                  backgroundColor: `${competitionColors.secondary}22`,
                                  borderColor: competitionColors.secondary,
                                  justifySelf: 'stretch' // Make chips stretch to fill grid cells
                                }}
                              />
                            ))}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Box>
                ) : (
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Rotation
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      No rotation assigned to this session
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setIsEditing(true)}
                      sx={{
                        mt: 1,
                        color: competitionColors.primary,
                        borderColor: competitionColors.primary,
                        '&:hover': {
                          borderColor: competitionColors.primary,
                          backgroundColor: `${competitionColors.primary}22`,
                        }
                      }}
                    >
                      Assign Rotation
                    </Button>
                  </Box>
                )}
              </Box>
            </Grid>

            {/* Actions Section */}
            <Grid item xs={12} md={4} sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1, p: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25 }}>
                Actions
              </Typography>

              {/* Manage Group - Now at the top with inline label */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', minWidth: 64, flexShrink: 0, textAlign: 'left' }}>
                  Manage:
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => setIsEditing(true)}
                    size="small"
                    sx={{
                      minWidth: 'auto',
                      px: 1.5,
                      py: 0.5,
                      fontSize: '0.75rem',
                      fontWeight: 400,
                      textTransform: 'none',
                      color: theme => theme.palette.mode === 'dark' ? '#aaa' : '#666',
                      borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.23)',
                      '&:hover': {
                        backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)',
                        borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.3)',
                      },
                    }}
                  >
                    Edit
                  </Button>
                  <Button
                    variant={scratchMode ? 'contained' : 'outlined'}
                    color={scratchMode ? 'error' : 'primary'}
                    startIcon={<CancelIcon />}
                    onClick={handleScratchButton}
                    size="small"
                    sx={{
                      minWidth: 'auto',
                      px: 1.5,
                      py: 0.5,
                      fontSize: '0.75rem',
                      fontWeight: 400,
                      textTransform: 'none',
                      ...(scratchMode && {
                        backgroundColor: competitionColors.primary,
                        color: 'white',
                        '&:hover': {
                          backgroundColor: competitionColors.primary,
                        },
                      }),
                      ...(!scratchMode && {
                        color: theme => theme.palette.mode === 'dark' ? '#aaa' : '#666',
                        borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.23)',
                        '&:hover': {
                          backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)',
                          borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.3)',
                        },
                      }),
                    }}
                  >
                    {scratchMode ? 'Done' : 'Scratch'}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<PrintIcon />}
                    onClick={() => setIsPrintPreviewOpen(true)}
                    size="small"
                    sx={{
                      minWidth: 'auto',
                      px: 1.5,
                      py: 0.5,
                      fontSize: '0.75rem',
                      fontWeight: 400,
                      textTransform: 'none',
                      color: theme => theme.palette.mode === 'dark' ? '#aaa' : '#666',
                      borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.23)',
                      '&:hover': {
                        backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)',
                        borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.3)',
                      },
                    }}
                  >
                    Print
                  </Button>
                </Box>
              </Box>

              {/* Display Group with inline label */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', minWidth: 64, flexShrink: 0, textAlign: 'left' }}>
                  Display:
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                  <Button
                    variant={highlightTeamsEnabled ? 'contained' : 'outlined'}
                    onClick={() => {
                      setHighlightTeamsEnabled(!highlightTeamsEnabled);
                      // When enabling teams, disable levels to avoid conflicts
                      if (!highlightTeamsEnabled) setHighlightLevelsEnabled(false);
                    }}
                    size="small"
                    sx={{
                      minWidth: 'auto',
                      px: 1.5,
                      py: 0.5,
                      fontSize: '0.75rem',
                      fontWeight: 400,
                      textTransform: 'none',
                      ...(highlightTeamsEnabled && {
                        backgroundColor: competitionColors.primary,
                        color: 'white',
                        '&:hover': {
                          backgroundColor: competitionColors.primary,
                        },
                      }),
                      ...(!highlightTeamsEnabled && {
                        color: theme => theme.palette.mode === 'dark' ? '#aaa' : '#666',
                        borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.23)',
                        '&:hover': {
                          backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)',
                          borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.3)',
                        },
                      }),
                    }}
                  >
                    Teams
                  </Button>
                  <Button
                    variant={highlightLevelsEnabled ? 'contained' : 'outlined'}
                    onClick={() => {
                      setHighlightLevelsEnabled(!highlightLevelsEnabled);
                      // When enabling levels, disable teams to avoid conflicts
                      if (!highlightLevelsEnabled) setHighlightTeamsEnabled(false);
                    }}
                    size="small"
                    sx={{
                      minWidth: 'auto',
                      px: 1.5,
                      py: 0.5,
                      fontSize: '0.75rem',
                      fontWeight: 400,
                      textTransform: 'none',
                      ...(highlightLevelsEnabled && {
                        backgroundColor: competitionColors.primary,
                        color: 'white',
                        '&:hover': {
                          backgroundColor: competitionColors.primary,
                        },
                      }),
                      ...(!highlightLevelsEnabled && {
                        color: theme => theme.palette.mode === 'dark' ? '#aaa' : '#666',
                        borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.23)',
                        '&:hover': {
                          backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)',
                          borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.3)',
                        },
                      }),
                    }}
                  >
                    Levels
                  </Button>
                  <Button
                    variant={viewByTeams ? 'contained' : 'outlined'}
                    onClick={() => setViewByTeams(!viewByTeams)}
                    size="small"
                    sx={{
                      minWidth: 'auto',
                      px: 1.5,
                      py: 0.5,
                      fontSize: '0.75rem',
                      fontWeight: 400,
                      textTransform: 'none',
                      ...(viewByTeams && {
                        backgroundColor: competitionColors.primary,
                        color: 'white',
                        '&:hover': {
                          backgroundColor: competitionColors.primary,
                        },
                      }),
                      ...(!viewByTeams && {
                        color: theme => theme.palette.mode === 'dark' ? '#aaa' : '#666',
                        borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.23)',
                        '&:hover': {
                          backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)',
                          borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.3)',
                        },
                      }),
                    }}
                  >
                    By Teams
                  </Button>
                  <Button
                    variant={showBreakdown ? 'contained' : 'outlined'}
                    onClick={() => setShowBreakdown(!showBreakdown)}
                    size="small"
                    sx={{
                      minWidth: 'auto',
                      px: 1.5,
                      py: 0.5,
                      fontSize: '0.75rem',
                      fontWeight: 400,
                      textTransform: 'none',
                      ...(showBreakdown && {
                        backgroundColor: competitionColors.primary,
                        color: 'white',
                        '&:hover': {
                          backgroundColor: competitionColors.primary,
                        },
                      }),
                      ...(!showBreakdown && {
                        color: theme => theme.palette.mode === 'dark' ? '#aaa' : '#666',
                        borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.23)',
                        '&:hover': {
                          backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)',
                          borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.3)',
                        },
                      }),
                    }}
                  >
                    Breakdown{selectedFilters.length > 0 ? ` (${selectedFilters.length})` : ''}
                  </Button>
                  {selectedFilters.length > 0 && (
                    <IconButton
                      onClick={() => setSelectedFilters([])}
                      size="small"
                      sx={{
                        ml: -0.5,
                        p: 0.25,
                        color: theme => theme.palette.mode === 'dark' ? '#aaa' : '#666',
                        '&:hover': {
                          backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)',
                        },
                      }}
                    >
                      <CloseIcon sx={{ fontSize: '0.875rem' }} />
                    </IconButton>
                  )}
                </Box>
              </Box>

              {/* Order Group with inline label */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', minWidth: 64, flexShrink: 0, textAlign: 'left' }}>
                  Order:
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                  <Button
                    variant={moveAthletesMode ? 'contained' : 'outlined'}
                    color={moveAthletesMode ? 'error' : 'primary'}
                    onClick={() => {
                      const newMode = !moveAthletesMode;
                      if (newMode) {
                         setGymnastSnapshot(JSON.parse(JSON.stringify(currentCompetition?.gymnasts || [])));
                         setMoveSquadsMode(false);
                      } else {
                         if (!hasAthleteOrderChanges()) {
                           dispatch(setToast({ message: 'No athlete order changes to save', severity: 'info' }));
                         }
                         setGymnastSnapshot(null);
                      }
                      setMoveAthletesMode(newMode);
                    }}
                    size="small"
                    sx={{
                      minWidth: 'auto',
                      px: 1.5,
                      py: 0.5,
                      fontSize: '0.75rem',
                      fontWeight: 400,
                      textTransform: 'none',
                      ...(!moveAthletesMode && {
                        color: theme => theme.palette.mode === 'dark' ? '#aaa' : '#666',
                        borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.23)',
                        '&:hover': {
                          backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)',
                          borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.3)',
                        },
                      }),
                    }}
                    >
                    {moveAthletesMode ? 'Done' : 'Athletes'}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      if (moveSquadsMode && rotationSnapshot) {
                        dispatch(updateCompetition({
                           ...currentCompetition,
                           rotations: rotationSnapshot
                        }));
                        dispatch(setToast({ message: 'Squad moves undone', severity: 'info' }));
                      } else if (moveAthletesMode && gymnastSnapshot) {
                        dispatch(updateCompetition({
                           type: 'gymnasts',
                           data: gymnastSnapshot,
                           showToast: true,
                           toastMessage: 'Athlete moves undone'
                        }));
                      } else {
                        // Full reset: clear athlete order AND squad order
                        const sessionGymnasts = currentCompetition?.gymnasts
                          ?.filter(g => Number(g.session) === Number(session.sessionNumber));
                        const otherGymnasts = currentCompetition?.gymnasts
                          ?.filter(g => Number(g.session) !== Number(session.sessionNumber));

                        const gymnastsToUpdate = sessionGymnasts?.map(gymnast => ({
                          ...gymnast,
                          order: null
                        })) || [];

                        // Reset squad assignments to default order (1, 2, 3, ...)
                        if (rotation) {
                          const defaultSquadOrder = Array.from(
                            { length: rotation.numSquads || 0 },
                            (_, i) => i + 1
                          ).join('');
                          const resetRotation = {
                            ...rotation,
                            squadAssignments: defaultSquadOrder ? [defaultSquadOrder] : []
                          };
                          const rotationIndex = currentCompetition.rotations.findIndex(r => r.id === rotation.id);
                          if (rotationIndex !== -1) {
                            const updatedRotations = [...currentCompetition.rotations];
                            updatedRotations[rotationIndex] = resetRotation;
                            // Dispatch rotation reset first
                            dispatch(updateCompetition({
                              ...currentCompetition,
                              rotations: updatedRotations
                            }));
                          }
                        }

                        // Then dispatch gymnast order reset
                        const allGymnasts = [...(otherGymnasts || []), ...gymnastsToUpdate];
                        dispatch(updateCompetition({
                          type: 'gymnasts',
                          data: allGymnasts,
                          showToast: true,
                          toastMessage: 'Squad and athlete order has been reset'
                        }));
                      }
                    }}
                    size="small"
                    sx={{
                      minWidth: 'auto',
                      px: 1.5,
                      py: 0.5,
                      fontSize: '0.75rem',
                      fontWeight: 400,
                      textTransform: 'none',
                      color: theme => theme.palette.mode === 'dark' ? '#aaa' : '#666',
                      borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.23)',
                      '&:hover': {
                        backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)',
                        borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.3)',
                      },
                    }}
                  >
                    {moveSquadsMode ? 'Undo' : moveAthletesMode ? 'Undo' : 'Reset'}
                  </Button>
                </Box>
              </Box>
            </Grid>
          </Grid>

        </CardContent>
      </Card>

      {/* Flights Section */}
      <Box sx={{ display: 'flex', gap: 2, position: 'relative' }}>
        <Paper
          variant="outlined"
          onClick={() => {
            if (moveAthletesMode) {
              setSelectedAthleteIds([]);
            }
          }}
          data-ui="sessions-squads-container"
          sx={{
            flex: 1,
            backgroundImage: moveSquadsMode
              ? getWatermarkSvg('Moving Squads')
              : moveAthletesMode
                 ? getWatermarkSvg('Moving Athletes')
                 : scratchMode
                    ? getWatermarkSvg('Scratch Mode')
                    : 'none',
            backgroundRepeat: (moveSquadsMode || moveAthletesMode || scratchMode) ? 'repeat' : 'unset',
          }}
        >
          {session.numFlights > 1 ? (
            // Always use unified view for multi-flight sessions to prevent reload on mode change
            <SessionSquad
              session={session}
              competitionColors={competitionColors}
              flightNumber={1}
              highlightedTeamId={highlightedTeamId}
              onTeamHighlight={setHighlightedTeamId}
              selectedRotation={selectedRotation}
              highlightTeamsEnabled={highlightTeamsEnabled}
              highlightLevelsEnabled={highlightLevelsEnabled}
              teamColors={teamColors}
              levelColors={levelColors}
              moveSquadsMode={moveSquadsMode}
              moveAthletesMode={moveAthletesMode}
              scratchMode={scratchMode}
              scratchSelections={scratchSelections}
              selectedAthleteIds={selectedAthleteIds}
              setSelectedAthleteIds={setSelectedAthleteIds}
              onToggleScratch={toggleScratchSelection}
              selectedFilters={selectedFilters}
              allFlights={true}
              viewByTeams={viewByTeams}
              onUpdateOrders={(updatedGymnasts) => {
                dispatch(updateCompetition({
                  type: 'gymnasts',
                  data: updatedGymnasts,
                  showToast: true
                }));
              }}
            />
          ) : (
            renderFlightSection(1)
          )}
        </Paper>
        
        {/* Rotation Selector */}
        <Paper
          variant="outlined"
          className="no-print"
          sx={{
            width: 60,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            pt: 2,
            px: 1,
            pb: 1,
            gap: 1,
            position: 'sticky',
            top: 173,
            alignSelf: 'flex-start',
            maxHeight: 'calc(100vh - 205px)',
            overflowY: 'auto'
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: 'text.primary',
              fontWeight: 500,
              marginBottom: 1
            }}
          >
            R
          </Typography>
          {Array.from({ length: rotation?.numSquads || (session.gender?.toLowerCase() === 'm' ? 6 : 4) }, (_, i) => i + 1).map((rotationNum) => (
            <Box
              key={rotationNum}
              sx={{
                width: '100%',
                borderLeft: 2,
                borderColor: selectedRotation === rotationNum ? competitionColors.primary : 'transparent',
                transition: 'all 0.2s ease-in-out',
                transform: selectedRotation === rotationNum ? 'scale(1.05)' : 'scale(1)',
                '& .MuiIconButton-root': {
                  transition: 'all 0.2s ease-in-out',
                }
              }}
            >
              <IconButton
                size="small"
                sx={{
                  width: 40,
                  height: 40,
                  // In dark mode, use white for selected rotation for better contrast
                  color: theme => selectedRotation === rotationNum
                    ? (theme.palette.mode === 'dark' ? '#fff' : competitionColors.primary)
                    : 'text.secondary',
                  '&:hover': {
                    backgroundColor: `${competitionColors.primary}11`
                  }
                }}
                onClick={() => {
                  setSelectedRotation(rotationNum);
                  // Update rotation and trigger re-render of squads
                  setSelectedRotation(rotationNum);
                }}
              >
                {rotationNum}
              </IconButton>
            </Box>
          ))}

          {/* Flight Navigation - Only show if session has multiple flights */}
          {session.numFlights > 1 && (
            <>
              <Divider sx={{ width: '80%', my: 1 }} />
              {Array.from({ length: session.numFlights }, (_, i) => i + 1).map((flightNum) => {
                const flightLetter = String.fromCharCode(64 + flightNum);
                return (
                  <Box
                    key={flightNum}
                    sx={{
                      width: '100%',
                      borderLeft: 2,
                      borderColor: activeFlight === flightNum ? competitionColors.primary : 'transparent',
                      transition: 'all 0.2s ease-in-out',
                      transform: activeFlight === flightNum ? 'scale(1.05)' : 'scale(1)',
                      '& .MuiIconButton-root': {
                        transition: 'all 0.2s ease-in-out',
                      }
                    }}
                  >
                    <IconButton
                      size="small"
                      sx={{
                        width: 40,
                        height: 40,
                        // In dark mode, use white for active flight for better contrast
                        color: theme => activeFlight === flightNum
                          ? (theme.palette.mode === 'dark' ? '#fff' : competitionColors.primary)
                          : 'text.secondary',
                        '&:hover': {
                          backgroundColor: `${competitionColors.primary}11`
                        }
                      }}
                      onClick={() => {
                        // Scroll to the flight section with offset
                        const flightElement = document.getElementById(`flight-${flightLetter}`);
                        if (flightElement) {
                          const elementTop = flightElement.getBoundingClientRect().top + window.scrollY;
                          const offsetTop = elementTop - 120; // Offset by 120 pixels down
                          window.scrollTo({
                            top: offsetTop,
                            behavior: 'smooth'
                          });
                        }
                      }}
                    >
                      {flightLetter}
                    </IconButton>
                  </Box>
                );
              })}
            </>
          )}
        </Paper>
      </Box>
      <SessionPrintPreview
        open={isPrintPreviewOpen}
        onClose={() => setIsPrintPreviewOpen(false)}
        session={session}
        viewByTeams={viewByTeams}
        selectedRotation={selectedRotation}
      />
    </Box>
  );
};

export default React.memo(SessionDetails);
